import React, { useEffect, useState } from 'react';
import {
  Drawer, Form, Input, Select, Row, Col, Radio, InputNumber, DatePicker,
  Button, Space, Divider, Typography, Tag, message, Modal, Alert,
} from 'antd';
import {
  UserOutlined, PhoneOutlined, MailOutlined, HomeOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { addLead, updateLead, checkDuplicate } from '../services/leadService';
import { EXPECTED_UNITS, PRIORITY_COLORS } from './leadConstants';
import RichTextEditor from '../../components/RichTextEditor';

const { TextArea } = Input;
const { Text } = Typography;

// Drawer for creating / editing a lead. Uses only antd components so it
// matches the rest of the application visually.
const LeadFormDrawer = ({ open, onClose, onSaved, editingLead, settings = {}, propertyTypes = [] }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [expectedStart, setExpectedStart] = useState('Immediately');
  const [dupWarning, setDupWarning] = useState(null);

  const sources = settings.lead_source || [];
  const situations = settings.current_situation || [];
  const statuses = settings.followup_status || [];
  const priorities = settings.priority_color || [];
  const tagPresets = settings.lead_tag || [];

  useEffect(() => {
    if (!open) return;
    setDupWarning(null);
    if (editingLead) {
      form.setFieldsValue({
        ...editingLead,
        NextFollowupAt: editingLead.NextFollowupAt ? dayjs(editingLead.NextFollowupAt) : null,
        Tags: Array.isArray(editingLead.Tags) ? editingLead.Tags : [],
      });
      setExpectedStart(editingLead.ExpectedStart || 'Immediately');
    } else {
      form.resetFields();
      form.setFieldsValue({ Priority: 'Warm', Status: 'New', ExpectedStart: 'Immediately' });
      setExpectedStart('Immediately');
    }
  }, [open, editingLead, form]);

  // Live duplicate detection on mobile blur
  const handleMobileBlur = async (e) => {
    const mobile = e.target.value && e.target.value.trim();
    if (!mobile) { setDupWarning(null); return; }
    try {
      const r = await checkDuplicate(mobile, editingLead ? editingLead.LeadID : undefined);
      setDupWarning(r.duplicate ? r.lead : null);
    } catch { /* non-blocking */ }
  };

  const buildPayload = (values) => ({
    ...values,
    NextFollowupAt: values.NextFollowupAt ? values.NextFollowupAt.format('YYYY-MM-DD HH:mm:ss') : null,
    Tags: values.Tags || [],
  });

  const doSave = async (payload) => {
    if (editingLead) {
      await updateLead(editingLead.LeadID, payload);
      message.success('Lead updated successfully');
    } else {
      await addLead(payload);
      message.success('Lead created successfully');
    }
    setSaving(false);
    onSaved && onSaved();
  };

  const handleSubmit = async () => {
    let values;
    try { values = await form.validateFields(); }
    catch { return; }
    setSaving(true);
    const payload = buildPayload(values);
    try {
      await doSave(payload);
    } catch (err) {
      setSaving(false);
      if (err.response && err.response.status === 409) {
        const existing = err.response.data && err.response.data.lead;
        Modal.confirm({
          title: 'Possible duplicate lead',
          content: `A lead with this mobile already exists${existing ? ` ("${existing.Name}")` : ''}. Create this lead anyway?`,
          okText: 'Create anyway',
          cancelText: 'Cancel',
          onOk: async () => {
            setSaving(true);
            try { await doSave({ ...payload, force: true }); }
            catch (e) { setSaving(false); message.error('Failed to create lead'); }
          },
        });
        return;
      }
      message.error(`Failed to ${editingLead ? 'update' : 'create'} lead`);
    }
  };

  return (
    <Drawer
      title={editingLead ? `Edit Lead — ${editingLead.Name}` : 'Add New Lead'}
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSubmit}>
            {editingLead ? 'Save Changes' : 'Create Lead'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Divider orientation="left" plain style={{ marginTop: 0 }}>Basic Details</Divider>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="Name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
              <Input prefix={<UserOutlined />} size="large" placeholder="Full name" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="Mobile"
              label="Mobile Number"
              rules={[
                { required: true, message: 'Please enter mobile number' },
                { pattern: /^[0-9+\-\s]{7,15}$/, message: 'Enter a valid number' },
              ]}
            >
              <Input prefix={<PhoneOutlined />} size="large" placeholder="Mobile" onBlur={handleMobileBlur} />
            </Form.Item>
          </Col>
        </Row>

        {dupWarning && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`A lead with this number already exists: ${dupWarning.Name} (#${dupWarning.LeadNo})`}
          />
        )}

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="AltMobile" label="Alternate Number">
              <Input prefix={<PhoneOutlined />} size="large" placeholder="Alternate number" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="Email" label="Email" rules={[{ type: 'email', message: 'Enter a valid email' }]}>
              <Input prefix={<MailOutlined />} size="large" placeholder="Email" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="City" label="City">
              <Input prefix={<EnvironmentOutlined />} size="large" placeholder="City" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="Address" label="Address">
              <Input prefix={<HomeOutlined />} size="large" placeholder="Address" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>Requirement</Divider>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="PropertyType" label="Property Type">
              <Select size="large" allowClear showSearch placeholder="Select property type"
                options={propertyTypes.map(p => ({ value: p, label: p }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="Source" label="Lead Source">
              <Select size="large" allowClear showSearch placeholder="Select source"
                options={sources.map(s => ({ value: s.Value, label: s.Label }))} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="CurrentSituation" label="Current Situation">
              <Select size="large" allowClear placeholder="Select current situation"
                options={situations.map(s => ({ value: s.Value, label: s.Label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="Priority" label="Priority">
              <Select size="large" placeholder="Priority"
                options={(priorities.length ? priorities.map(p => ({ value: p.Value, label: p.Label, color: p.Color }))
                  : Object.keys(PRIORITY_COLORS).map(k => ({ value: k, label: k, color: PRIORITY_COLORS[k] })))
                  .map(o => ({
                    value: o.value,
                    label: (
                      <span>
                        <span style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                          background: o.color || PRIORITY_COLORS[o.value] || '#999', marginRight: 8,
                        }} />
                        {o.label}
                      </span>
                    ),
                  }))} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="ExpectedStart" label="Expected Work Start">
          <Radio.Group onChange={(e) => setExpectedStart(e.target.value)}>
            <Radio value="Immediately">Immediately</Radio>
            <Radio value="After Some Time">After Some Time</Radio>
          </Radio.Group>
        </Form.Item>
        {expectedStart === 'After Some Time' && (
          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <Form.Item name="ExpectedStartValue" label="In"
                rules={[{ required: true, message: 'Enter a number' }]}>
                <InputNumber min={1} size="large" style={{ width: '100%' }} placeholder="e.g. 2" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="ExpectedStartUnit" label="Unit"
                rules={[{ required: true, message: 'Select unit' }]}>
                <Select size="large" options={EXPECTED_UNITS.map(u => ({ value: u, label: u }))} placeholder="Unit" />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Divider orientation="left" plain>Tracking</Divider>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="Status" label="Status">
              <Select size="large" placeholder="Status" showSearch
                options={[{ value: 'New', label: 'New' },
                  ...statuses.map(s => ({ value: s.Value, label: s.Label }))]} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="NextFollowupAt" label="Next Follow-up">
              <DatePicker showTime size="large" style={{ width: '100%' }} format="DD MMM YYYY, hh:mm A" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="AssignedUser" label="Assigned User">
              <Input size="large" placeholder="Assigned to" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="Tags" label="Tags">
              <Select mode="tags" size="large" placeholder="Add tags"
                options={tagPresets.map(t => ({ value: t.Value, label: t.Label }))}
                tagRender={({ label, value, closable, onClose }) => {
                  const preset = tagPresets.find(t => t.Value === value);
                  return (
                    <Tag color={preset ? preset.Color : 'default'} closable={closable} onClose={onClose}
                      style={{ marginInlineEnd: 4 }}>
                      {label}
                    </Tag>
                  );
                }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="Notes" label="Notes">
          <RichTextEditor placeholder="Detailed notes about the lead… (bold, lists, etc.)" />
        </Form.Item>

        <Text type="secondary" style={{ fontSize: 12 }}>
          Documents can be attached from the lead's detail page (Documents tab).
        </Text>
      </Form>
    </Drawer>
  );
};

export default LeadFormDrawer;
